/**
 * Normalize CKEditor announcement HTML for display.
 *
 * React's divarea emoji picker historically wrapped each emoji in its own
 * `<p>` (or separated them with `<br>`), which stacks vertically. Angular
 * typically keeps them inline on one line — collapse emoji-only blocks so the
 * acknowledgement popup matches.
 */
const EMOJI_ONLY_RE = /^[\s\p{Extended_Pictographic}\uFE0F\u200D\u20E3]*$/u

function isEmojiOnlyText(text: string): boolean {
  const trimmed = text.trim()
  return Boolean(trimmed) && EMOJI_ONLY_RE.test(text)
}

function isEmojiOnlyParagraph(el: Element): boolean {
  if (el.tagName !== 'P') return false
  const clone = el.cloneNode(true) as Element
  clone.querySelectorAll('br').forEach((br) => br.replaceWith(''))
  return isEmojiOnlyText(clone.textContent ?? '')
}

export function normalizeAnnouncementDescriptionHtml(html: string): string {
  if (!html?.trim()) return html
  if (typeof DOMParser === 'undefined') return html

  try {
    const doc = new DOMParser().parseFromString(`<div id="__announcement_root">${html}</div>`, 'text/html')
    const root = doc.getElementById('__announcement_root')
    if (!root) return html

    // Remove <br> inside emoji-only paragraphs so ❤️<br>❤️ becomes ❤️❤️
    root.querySelectorAll('p').forEach((p) => {
      if (!isEmojiOnlyParagraph(p)) return
      p.querySelectorAll('br').forEach((br) => br.remove())
    })

    const out: Node[] = []
    let emojiBuf: string[] = []

    const flushEmojis = () => {
      if (!emojiBuf.length) return
      const p = doc.createElement('p')
      p.textContent = emojiBuf.join('')
      out.push(p)
      emojiBuf = []
    }

    Array.from(root.childNodes).forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE && isEmojiOnlyParagraph(node as Element)) {
        emojiBuf.push(((node as Element).textContent ?? '').replace(/\s+/g, '').trim())
        return
      }
      if (node.nodeType === Node.TEXT_NODE && !(node.textContent ?? '').trim()) {
        return
      }
      flushEmojis()
      out.push(node.cloneNode(true))
    })
    flushEmojis()

    root.replaceChildren(...out)
    return root.innerHTML
  } catch {
    return html
  }
}
